/** Построение маршрутного графа из RVF network + правила проходимости рёбер. */
import { haversineM, projectOnSegment } from '@app/geo'
import type { GraphEdge, GraphNode, Network } from './types'

export type RouteProfile = 'foot' | 'vehicle' | 'truck' | 'wheelchair'

export interface Adjacent {
  readonly to: string
  readonly distM: number
  readonly edge: GraphEdge
  /** true, если ребро проходится против направления from→to. */
  readonly reverse: boolean
}

export interface BuiltGraph {
  readonly nodes: ReadonlyMap<string, GraphNode>
  readonly adj: ReadonlyMap<string, readonly Adjacent[]>
  /** Предпочтённый подъём (BuildGraphOpts). */
  readonly prefer?: ClimbPref
}

/**
 * Проходимость ребра профилем. Пешеход игнорирует oneway (это правило для
 * транспорта); коляска требует явного wheelchair=true; транспорт — vehicle=true
 * и прямого направления на oneway-рёбрах; грузовой — как транспорт, но ребро
 * можно закрыть для фур (truck=false) или открыть только для них (truck=true
 * при vehicle=false: весовая площадка, грузовой двор).
 */
export function edgeAllows(
  edge: GraphEdge,
  profile: RouteProfile,
  reverse: boolean,
  strict = false,
): boolean {
  // Синтетический коннектор «последние метры к входу» (buildGraph): его флаги
  // vehicle/truck/wheelchair проставлены ПО ТИПУ узла (дверь → пешеход+фура,
  // но НЕ авто: машина не въезжает в дверь, а паркуется — park-and-walk).
  switch (profile) {
    case 'foot':
      // Инклюзивно (дефолт): пешеход ходит везде, в т.ч. по грузовой дороге.
      // Строго: только пешеходные дорожки (vehicle не проставлен).
      return strict ? edge.vehicle !== true : true
    case 'wheelchair':
      // Ровный путь проходим для коляски, ПОКА не сказано обратное. Требование
      // явного `true` означало, что маршрут для коляски не строился ни на одной
      // карте, нарисованной в редакторе: тот проставлял всем рёбрам `false`.
      // Непроходимое (лестница, порог) помечается явно: `wheelchair: false`.
      return edge.wheelchair !== false
    case 'vehicle':
      return edge.vehicle === true && !(edge.oneway === true && reverse)
    case 'truck':
      // Строго: только явно грузовые; иначе фолбэк на любые проезжие.
      return (
        (strict ? edge.truck === true : (edge.truck ?? edge.vehicle) === true) &&
        !(edge.oneway === true && reverse)
      )
  }
}

/**
 * Предпочтение «своего» типа дороги (решение владельца 25.07.2026): если всё
 * проложено грузовыми, но есть пешеходный/легковой кусок — для этого профиля
 * он дешевле и выигрывает; чужая (но разрешённая) дорога дороже, а не запрещена.
 * Множители ≥1 сохраняют допустимость эвристики A* (прямая — нижняя граница).
 */
export function edgeProfileFactor(edge: GraphEdge, profile: RouteProfile): number {
  switch (profile) {
    case 'foot':
      return edge.vehicle === true ? 1.4 : 1
    case 'wheelchair':
      return 1
    case 'vehicle':
      return edge.truck === true ? 1.15 : 1
    case 'truck':
      return edge.truck === true ? 1 : 1.2
  }
}

/**
 * Штраф межуровневого ребра, м-эквивалент (BACKLOG №9): ожидание лифта
 * дороже пролёта лестницы, эскалатор почти бесплатен. Ребро в пределах
 * уровня — чистая дистанция.
 */
const CLIMB_PENALTY_M: Readonly<Record<string, number>> = {
  elevator: 30,
  escalator: 5,
  stairs: 10,
}
const CLIMB_PENALTY_DEFAULT_M = 15

/** Предпочтение способа подъёма (запрос владельца 31.07): человек в карточке
 *  цели выбирает «лифт / эскалатор / лестница», и маршрут ведёт через него. */
export type ClimbPref = 'elevator' | 'escalator' | 'stairs'

/**
 * Цена «не того» подъёма при явном предпочтении: человек, выбравший лифт,
 * согласен на крюк такого размера, лишь бы не по лестнице. НЕ бесконечность:
 * если предпочтённого подъёма нет вовсе, маршрут всё равно построится, а не
 * «не найден». Запрет — это профиль (wheelchair), предпочтение — только вес.
 */
const NON_PREFERRED_DETOUR_M = 250

function climbExtraM(kind: string, base: number, prefer: ClimbPref | undefined): number {
  if (!prefer) return base
  if (kind === prefer) return base * 0.25
  return base + NON_PREFERRED_DETOUR_M
}

/** Стоимость ребра a→b с учётом способа подъёма (узлы-концы ребра). */
export function edgeCostM(distM: number, a: GraphNode, b: GraphNode, prefer?: ClimbPref): number {
  if (a.level === b.level) return distM
  const kind = a.kind in CLIMB_PENALTY_M ? a.kind : b.kind
  const base = CLIMB_PENALTY_M[kind] ?? CLIMB_PENALTY_DEFAULT_M
  return distM + climbExtraM(kind, base, prefer)
}

/** Узлы доступа, которые ОБЯЗАНЫ быть на сети (иначе роутер их не видит). */
const ACCESS_KINDS = new Set<string>(['door', 'entrance', 'dock', 'gate'])
/** Дальше этого перпендикуляра к дороге дверь не привязываем (данные битые). */
const AUTO_CONNECT_MAX_M = 30

/** Узел-вход здания (дверь/вход/рампа/КПП): его авто-привязываем к дороге.
 *  POI-семена (парковка и пр.) НЕ трогаем — у них честный фолбэк-пунктир. */
function isAccessNode(node: GraphNode): boolean {
  return ACCESS_KINDS.has(node.kind)
}

/**
 * Профили коннектора «последние метры» по типу узла (park-and-walk):
 * дверь/вход → пешеход+фура (грузовая дверь), но НЕ авто — машина паркуется;
 * рампа(dock) → фура+пешеход; КПП(gate) → все (пропускной пункт).
 * Поле vehicle НЕ ставим у двери/рампы → авто не «доезжает» до входа.
 */
function connectorFlags(kind: string): Partial<GraphEdge> {
  if (kind === 'gate') return { vehicle: true, truck: true, wheelchair: true }
  return { truck: true, wheelchair: true }
}

/** Ближайшее ребро (перпендикуляр) к точке двери; null — дальше порога. */
function nearestEdgeFoot(
  nodes: Map<string, GraphNode>,
  edges: readonly GraphEdge[],
  from: GraphNode,
): { edge: GraphEdge; foot: GraphNode['point']; distM: number } | null {
  let best: { edge: GraphEdge; foot: GraphNode['point']; distM: number } | null = null
  for (const edge of edges) {
    const a = nodes.get(edge.from)
    const b = nodes.get(edge.to)
    if (!a || !b) continue
    const proj = projectOnSegment(from.point, a.point, b.point)
    if (proj.distanceM <= AUTO_CONNECT_MAX_M && (!best || proj.distanceM < best.distM)) {
      best = { edge, foot: proj.point, distM: proj.distanceM }
    }
  }
  return best
}

/**
 * Авто-привязка висящих узлов доступа к дороге (research/19: «дверь должна быть
 * на сети»): дверь/вход/рампа/КПП без единого ребра проецируется ПЕРПЕНДИКУЛЯРОМ
 * на ближайшее ребро (≤30 м). В точке проекции ставится узел, соединённый с
 * обоими концами ребра, а дверь — коротким connector к нему. Так маршрут выходит
 * с дороги РЯДОМ с дверью (без «доезжаю до дальнего узла и возвращаюсь»).
 */
function autoConnectAccessNodes(
  nodes: Map<string, GraphNode>,
  edges: readonly GraphEdge[],
  adj: Map<string, Adjacent[]>,
  push: (from: string, entry: Adjacent) => void,
): void {
  const connected = new Set(adj.keys())
  for (const node of [...nodes.values()]) {
    if (connected.has(node.id) || !isAccessNode(node)) continue
    const near = nearestEdgeFoot(nodes, edges, node)
    if (!near) continue
    const a = nodes.get(near.edge.from)
    const b = nodes.get(near.edge.to)
    if (!a || !b) continue
    // Узел в перпендикулярной проекции, вклеен между концами ребра (a-b остаётся).
    const foot: GraphNode = {
      id: `${node.id}~c`,
      level: node.level,
      point: near.foot,
      kind: 'junction',
    }
    nodes.set(foot.id, foot)
    for (const end of [a, b]) {
      const distM = haversineM(foot.point, end.point)
      push(end.id, { to: foot.id, distM, edge: near.edge, reverse: false })
      push(foot.id, { to: end.id, distM, edge: near.edge, reverse: true })
    }
    const conn: GraphEdge = {
      from: node.id,
      to: foot.id,
      connector: true,
      ...connectorFlags(node.kind),
    }
    push(node.id, { to: foot.id, distM: near.distM, edge: conn, reverse: false })
    push(foot.id, { to: node.id, distM: near.distM, edge: conn, reverse: true })
  }
}

/**
 * Размечена ли доступность пути ЯВНО. Нужно, чтобы не выдавать предположение за
 * факт: маршрут мы строим и без разметки (иначе он не строится вовсе), но
 * говорить «без ступеней» вправе только там, где владелец это подтвердил.
 */
export function accessVerified(edges: readonly GraphEdge[]): boolean {
  return edges.some((e) => e.wheelchair === true)
}

/** Подъёмы, по которым коляска не проедет: лестница и эскалатор. */
const NO_WHEELCHAIR_CLIMB = new Set(['stairs', 'escalator'])

/**
 * Проставить доступность подъёма, если она не задана в данных.
 *
 * Полагаться на разметку каждого ребра нельзя: владельцы её почти не заполняют,
 * а требование явного «да» означало, что маршрут для коляски не строился нигде.
 * Поэтому: ровный путь доступен, пока не сказано обратное, а межэтажный —
 * по типу подъёма (лифт — да, лестница и эскалатор — нет).
 */
function withAccess(edge: GraphEdge, a: GraphNode, b: GraphNode): GraphEdge {
  if (edge.wheelchair !== undefined) return edge
  if (a.level === b.level) return edge
  const climb = NO_WHEELCHAIR_CLIMB.has(a.kind) || NO_WHEELCHAIR_CLIMB.has(b.kind)
  return climb ? { ...edge, wheelchair: false } : edge
}

export interface BuildGraphOpts {
  /** Предпочтённый способ подъёма — вес, не запрет (climbBias). */
  readonly prefer?: ClimbPref
}

export function buildGraph(network: Network, opts: BuildGraphOpts = {}): BuiltGraph {
  const nodes = new Map<string, GraphNode>()
  for (const node of network.nodes) nodes.set(node.id, node)

  const adj = new Map<string, Adjacent[]>()
  const push = (from: string, entry: Adjacent): void => {
    const list = adj.get(from)
    if (list) list.push(entry)
    else adj.set(from, [entry])
  }

  for (const edge of network.edges) {
    const a = nodes.get(edge.from)
    const b = nodes.get(edge.to)
    if (!a || !b) continue
    const distM = haversineM(a.point, b.point)
    const ready = withAccess(edge, a, b)
    push(edge.from, { to: edge.to, distM, edge: ready, reverse: false })
    push(edge.to, { to: edge.from, distM, edge: ready, reverse: true })
  }

  autoConnectAccessNodes(nodes, network.edges, adj, push)
  // Предпочтение живёт НА графе: вьюер пересобирает граф при смене выбора
  // (сотни узлов — микросекунды), а A* читает его без прокидывания через
  // шесть сигнатур.
  return opts.prefer ? { nodes, adj, prefer: opts.prefer } : { nodes, adj }
}

/**
 * Есть ли на пути транспортные рёбра. Ответ на вопрос карточки цели «уместен
 * ли выбор Пешком/Авто/Фура»: внутри здания дорог нет — выбор не нужен;
 * маршрут через территорию содержит дорогу — есть чем ехать (03.08.2026).
 */
export function routeUsesRoads(graph: BuiltGraph, nodeIds: readonly string[]): boolean {
  for (let i = 0; i < nodeIds.length - 1; i++) {
    const from = nodeIds[i]
    const to = nodeIds[i + 1]
    if (from === undefined || to === undefined) continue
    const hop = graph.adj.get(from)?.find((a) => a.to === to)
    if (hop && (hop.edge.vehicle === true || hop.edge.truck === true)) return true
  }
  return false
}
